import React, { useCallback, useEffect, useState } from 'react';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import apiFetch from '../utils/apiFetch';

// KPI ダッシュボード（Backlog T-3 / 資料 通し22枚目）。
//
// **母数が小さい期間はそう表示する**のが完了条件。ユーザー6人の時期に
// 「チェックイン率 50%」と出すと、2件中1件でしかないものが指標として読まれ、
// 翌週 0% になったときに「半減した」と言うことになる。
//
// だからこの画面は:
// - **率と一緒に必ず分子/分母を出す**
// - 母数が閾値に届かない週は率を出さず、件数だけを出す
// - **今週は「途中」と書く** —— 前の週と並べると必ず低く見える
//
// 閾値はサーバが返す（domain.MinReliableDenominator）。**画面が別の数を
// 持たない** —— 片方だけ変えると、色のついた数字と根拠がずれる。

const LABEL = {
  check_in_rate: 'チェックイン率',
  checks_per_visit: '1来店あたり確認件数',
  help_join_rate: '招待→参加率',
  recap_open_rate: 'リキャップ開封率',
  return_rate_30d: '30日再訪率',
  updates_per_user: '1人あたり更新件数',
};

const HELP = {
  check_in_rate: '店舗ページを開いた人のうち、実際にチェックインした人',
  checks_per_visit: '来店1回あたり、何件の景品を確認したか',
  help_join_rate: 'お助け依頼が届いた人のうち、参加した人',
  recap_open_rate: '来店のあった日のうち、振り返りを開いた日',
  return_rate_30d: '初めて来た人のうち、30日以内にまた来た人',
  updates_per_user: '活動した人1人あたり、DBを良くした行為の件数',
};

const KEYS = Object.keys(LABEL);

export default function KpiDashboard() {
  useAuthGuard();

  const [weeks, setWeeks] = useState([]);
  const [minDenominator, setMinDenominator] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/kpi/weeks?weeks=8`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setWeeks(body?.data?.weeks || []);
      if (body?.data?.minReliableDenominator) {
        setMinDenominator(body.data.minReliableDenominator);
      }
    } catch (e) {
      setError(e.message || '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h1 className="h4 mb-1">KPI</h1>
          <p className="text-muted small mb-0">
            資料 通し22枚目の6本。週次（月曜始まり・JST）。
          </p>
        </div>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={load}>
          再読み込み
        </button>
      </div>

      {/* **読み方を先に書く。** 数字だけ並べると、母数の小さい週の率が
          そのまま指標として引用される */}
      <div className="alert alert-secondary py-2 small">
        母数が <strong>{minDenominator}件</strong> に満たない週は、率ではなく件数だけを
        出しています。1件動いただけで数字が跳ねる範囲なので、率として読むと必ず
        誤解します。
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {loading ? (
        <div className="text-muted">読み込み中…</div>
      ) : weeks.length === 0 ? (
        <div className="alert alert-secondary">まだデータがありません。</div>
      ) : (
        <div className="card">
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ minWidth: 200 }}>指標</th>
                  {weeks.map((w) => (
                    <th key={w.weekStart} className="text-end text-nowrap">
                      {w.weekStart.slice(5)}
                      {/* **今週はまだ終わっていない。** 前の週と並べると必ず
                          低く見えるので、そう書く */}
                      {w.partial && (
                        <div className="text-muted fw-normal" style={{ fontSize: '0.7rem' }}>
                          途中
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {KEYS.map((key) => (
                  <tr key={key}>
                    <td>
                      <div className="fw-semibold">{LABEL[key]}</div>
                      <div className="text-muted small">{HELP[key]}</div>
                    </td>
                    {weeks.map((w) => (
                      <td key={w.weekStart} className="text-end text-nowrap">
                        <Cell
                          value={(w.values || []).find((v) => v.key === key)}
                          minDenominator={minDenominator}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Layout>
  );
}

function Cell({ value, minDenominator }) {
  if (!value) return <span className="text-muted">—</span>;

  const { numerator, denominator, rate } = value;

  // 分母が0なら、率でも平均でも言えることが無い
  if (denominator === 0) {
    return <span className="text-muted">—</span>;
  }

  const reliable = denominator >= minDenominator;
  const computed = numerator / denominator;

  return (
    <>
      {/* **母数が足りない週は率を出さない。** 出した瞬間に引用される */}
      {reliable ? (
        <div className="fw-semibold">
          {rate ? `${Math.round(computed * 100)}%` : computed.toFixed(1)}
        </div>
      ) : (
        <div className="text-muted" title="母数が少ないため率を出していません">
          —
        </div>
      )}
      {/* **分子/分母を必ず添える。** これが「50%」の中身を見せる唯一の手段 */}
      <div className="text-muted" style={{ fontSize: '0.75rem' }}>
        {numerator.toLocaleString()}/{denominator.toLocaleString()}
      </div>
    </>
  );
}
